import Resource from './resource';
import request from '@/utils/request';

/**
 * Map frontend notification types to backend codes
 * Frontend: 'info', 'success', 'warning', 'error'
 * Backend: '0', '1', '2', '3'
 */
const mapNotificationType = (frontendType) => {
  const typeMap = {
    'info': '0',
    'success': '1', 
    'warning': '2',
    'error': '3'
  };
  return typeMap[frontendType] || '0'; // Default to 'info' if unknown
};

/**
 * Map backend notification codes to frontend types
 * Backend: '0', '1', '2', '3'
 * Frontend: 'info', 'success', 'warning', 'error'
 */
const mapBackendNotificationType = (backendType) => {
  const typeMap = {
    '0': 'info',
    '1': 'success',
    '2': 'warning', 
    '3': 'error'
  };
  return typeMap[backendType] || 'info'; // Default to 'info' if unknown
};

class NotificationResource extends Resource {
  constructor() {
    super('user_notifications');
  }

  /**
   * Get notifications for the current user
   * @param {Object} query - Query parameters
   * @returns {Promise} API response
   */
  getUserNotifications(query = {}) {
    return this.list(query);
  }

  /**
   * Create a new notification
   * @param {Object} notification - Notification data
   * @returns {Promise} API response
   */
  createNotification(notification) {
    // Map the notification type before sending to backend
    const mappedNotification = {
      ...notification,
      type: mapNotificationType(notification.type)
    };
    return this.store(mappedNotification);
  }

  /**
   * Mark a notification as read
   * @param {number|string} id - Notification ID
   * @returns {Promise} API response
   */
  markAsRead(id) {
    return request({
      url: `/${this.uri}/${id}/mark_read/`,
      method: 'post',
    });
  }

  /**
   * Mark all notifications as read for the current user
   * @returns {Promise} API response
   */
  markAllAsRead() {
    return request({
      url: `/${this.uri}/mark_all_read/`,
      method: 'post',
    });
  }

  /**
   * Delete a notification
   * @param {number|string} id - Notification ID
   * @returns {Promise} API response
   */
  deleteNotification(id) {
    return this.destroy(id);
  }

  /**
   * Clear all notifications for the current user
   * @returns {Promise} API response
   */
  clearAll() {
    return request({
      url: `/${this.uri}/clear_all/`,
      method: 'post',
    });
  }

  /**
   * Get unread notification count
   * @returns {Promise} API response
   */
  getUnreadCount() {
    return request({
      url: `/${this.uri}/unread_count/`,
      method: 'get',
    });
  }

  /**
   * Send notification to specific users
   * @param {Object} data - Notification data
   * @param {string} data.message - Notification message
   * @param {string} data.type - Notification type (info, success, warning, error)
   * @param {Array} data.user_ids - Array of user IDs to send notification to
   * @param {string} [data.title] - Notification title
   * @param {string} [data.url] - Notification url
   * @param {string} [data.factory_id] - Notification factory id
   * @param {string} [data.role_id] - Notification role id
   * @returns {Promise} API response
   */
  sendToUsers(data) {
    // Map the notification type before sending to backend
    const mappedData = {
      ...data,
      type: mapNotificationType(data.type)
    };
    return request({
      url: `/${this.uri}/send_to_users/`,
      method: 'post',
      data: mappedData,
    });
  }

  /**
   * Send notification to all users
   * @param {Object} data - Notification data
   * @param {string} data.message - Notification message
   * @param {string} data.type - Notification type (info, success, warning, error)
   * @param {string} [data.title] - Notification title
   * @param {string} [data.factory_id] - Notification factory id
   * @param {string} [data.role_id] - Notification role id
   * @returns {Promise} API response
   */
  sendToAll(data) {
    // Map the notification type before sending to backend
    const mappedData = {
      ...data,
      type: mapNotificationType(data.type)
    };
    return request({
      url: `/${this.uri}/send_to_all/`,
      method: 'post',
      data: mappedData,
    });
  }


  /**
   * Get user notification settings
   * @returns {Promise} API response
   */
  getUserSettings() {
    return request({
      url: `/user_settings/`,
      method: 'get',
    });
  }

  /**
   * Update user notification setting
   * @param {Object} data - Notification data
   * @param {string} data.notification - Notification setting
   * @returns {Promise} API response
   */
  updateUserNotificationSetting(data) {
    return request({
      url: `/user_settings/`,
      method: 'post',
      data: data,
    });
  }
}

export default NotificationResource;

// Export mapping functions for use in other modules
export { mapNotificationType, mapBackendNotificationType };
